// WebSocketContext.tsx
import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { WebSocketStatus, WebSocketHandlers } from '../hooks/websocket/types';
import { useWebSocket } from '../hooks/websocket/useWebSocket';

interface WebSocketContextType {
  status: WebSocketStatus;
  isConnected: boolean;
  registerHandler: (handler: WebSocketHandlers) => void;
  unregisterHandler: (handler: WebSocketHandlers) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [handlers, setHandlers] = useState<WebSocketHandlers[]>([]);

  const onFileChange = useCallback(async () => {
    // Execute all registered handlers
    for (const handler of handlers) {
      if (handler.onFileChange) {
        await handler.onFileChange();
      }
    }
  }, [handlers]); // 'handlers' is a dependency here

  // Memoize the handlers object
  const memoizedHandlers = useMemo(() => ({ onFileChange }), [onFileChange]);

  const { status, isConnected } = useWebSocket(memoizedHandlers);

  const registerHandler = useCallback((handler: WebSocketHandlers) => {
    setHandlers(prev => [...prev, handler]);
  }, []);

  const unregisterHandler = useCallback((handler: WebSocketHandlers) => {
    setHandlers(prev => prev.filter(h => h !== handler));
  }, []);

  return (
    <WebSocketContext.Provider value={{
      status,
      isConnected,
      registerHandler,
      unregisterHandler
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};
